import { SubmitInfo, SubmitInfoCourse } from "@app/core/domain/submit-proposal";
import { SubmitProposalStateService } from "@app/features/submit-proposal/services/submit-proposal-state.service";
import { TranslateService } from "@ngx-translate/core";
import { CourseDataMapper } from "@shared/services/course-data-mapping.service";
import { CourseData } from "@shared/types/course-data";

export interface SelectedCourseGroup {
  title: string;
  courseDataList: CourseData[];
}

export class SubmitConfirmCourseListBuilder {
  constructor(
    private readonly translate: TranslateService,
    private readonly courseDataMapper: CourseDataMapper,
    private readonly submitProposalStateService: SubmitProposalStateService,
  ) {}

  buildElectivesCourseGroups(submitInfo: SubmitInfo): SelectedCourseGroup[] {
    const electivesCoursesGroup = this.buildEmptyCourseGroup(this.translate.instant("submitProposal.courses"));

    const individualArrangementCourses = this.extractIndividualArrangement(submitInfo);
    const minorCourseGroups = this.extractMinorCourseGroups(electivesCoursesGroup);
    this.extractElectivesCourses(submitInfo, electivesCoursesGroup);

    return [...individualArrangementCourses, ...minorCourseGroups, electivesCoursesGroup];
  }

  private extractIndividualArrangement(submitInfo: SubmitInfo): SelectedCourseGroup[] {
    const groups: SelectedCourseGroup[] = [];

    if (submitInfo.electives.individualArrangements?.length) {
      const group = this.buildEmptyCourseGroup(this.translate.instant("submitProposal.coursesIndividualArrangements"));
      submitInfo.electives.individualArrangements.forEach((course) => {
        group.courseDataList.push(this.courseDataMapper.mapSubmitInfoCourseData(course));
      });

      if (group.courseDataList.length) {
        groups.push(group);
      }
    }

    return groups;
  }

  // Add all selected minor courses to minor group,
  // except when minor points not reached while student does not place courses in exam components.
  // In that case add selected courses to "other" electives courses group.
  private extractMinorCourseGroups(otherElectivesCoursesGroup: SelectedCourseGroup): SelectedCourseGroup[] {
    const minorGroups: SelectedCourseGroup[] = [];
    this.submitProposalStateService.minorsInProposal
      .filter((minorInProposal) => minorInProposal.selectedCourses.length)
      .forEach((minorInProposal) => {
        if (
          // if SVO places courses in exam components, minor should only be submitted if minimum points are reached
          !this.submitProposalStateService.planAllowsChangeExamComponent() &&
          minorInProposal.totalPoints < minorInProposal.minimumPoints
        ) {
          // Minor minimum points not reached, courses will be added as separate courses
          otherElectivesCoursesGroup.courseDataList.push(
            ...minorInProposal.selectedCourses.map((course) => this.courseDataMapper.mapSubmitInfoCourseData(course)),
          );
          // warning is handled in submitProposalStateService.buildWarningsMinorCoursesChecked
        } else {
          const minorGroup = this.buildEmptyCourseGroup(minorInProposal.name);
          minorGroup.courseDataList.push(
            ...minorInProposal.selectedCourses.map((course) => this.courseDataMapper.mapSubmitInfoCourseData(course)),
          );
          minorGroups.push(minorGroup);
        }
      });

    return minorGroups;
  }

  private extractElectivesCourses(submitInfo: SubmitInfo, otherElectivesCoursesGroup: SelectedCourseGroup): void {
    let selectedElectivesCourses: SubmitInfoCourse[] = [];
    selectedElectivesCourses = submitInfo.electives.courses.filter((electivesCourse) =>
      this.submitProposalStateService.isCourseSelected(electivesCourse),
    );
    selectedElectivesCourses.forEach((selectedElectivesCourse) => {
      otherElectivesCoursesGroup.courseDataList.push(
        this.courseDataMapper.mapSubmitInfoCourseData(selectedElectivesCourse),
      );
    });
  }

  private buildEmptyCourseGroup(header: string): SelectedCourseGroup {
    return {
      title: header,
      courseDataList: [],
    };
  }
}
